import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { SidebarSearch } from 'components/search_sidebar/sidebar_search';
import { Button } from 'components/ui/button';
import FieldSection, { baseFieldsArrayShape } from './field_section';
import { get, patch } from 'helpers/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import { faUndo } from '@fortawesome/pro-solid-svg-icons';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { fieldUniqueIdentifier, modifyBaseFields, SECTION_ORDER, setupModifiedFieldsState } from './helper';
import { showSnackbar } from 'components/ui/snackbar/helper';

export default function CompanyRequiredFields({
  baseFields,
  companyOverrides,
  companyUpdatePath,
  removeModelFieldTooltip,
  modelFieldRequirementsPath
}) {
  const [modifiedFields, setModifiedFields] = useState(() => setupModifiedFieldsState(companyOverrides));
  const [searchFilter, setSearchFilter] = useState('');
  const [savingChanges, setSavingChanges] = useState(false);
  const baseFieldsModified = modifyBaseFields(baseFields);

  function toggleField(field, isChecked) {
    const uniqueIdentifier = fieldUniqueIdentifier(field);
    const newValue = isChecked ? 'optional_field' : 'required_field';
    const previousValue = modifiedFields[uniqueIdentifier] || {};
    const updatedValue = {
      ...previousValue,
      nameOfModel: field.nameOfModel,
      fieldName: field.fieldName,
      status: newValue
    };
    setModifiedFields({ ...modifiedFields, [uniqueIdentifier]: updatedValue });
  }

  async function saveChanges() {
    setSavingChanges(true);

    const paramsForUpdate = Object.values(modifiedFields).reduce((fieldParams, modifiedField) => {
      const baseField = baseFieldsModified.find((field) => {
        return field.nameOfModel === modifiedField.nameOfModel && field.fieldName === modifiedField.fieldName;
      });

      if (baseField.status === modifiedField.status && modifiedField.hasOwnProperty('id')) {
        fieldParams.push({ ...modifiedField, _destroy: true });
      } else if (baseField.status !== modifiedField.status) {
        fieldParams.push(modifiedField);
      }

      return fieldParams;
    }, []);

    try {
      await patch(companyUpdatePath, { company: { model_field_requirements_attributes: paramsForUpdate } });
      const updatedCompanyOverrides = await get(modelFieldRequirementsPath);
      setModifiedFields(setupModifiedFieldsState(updatedCompanyOverrides));
      showSnackbar({ text: 'Changes successfully saved', variant: 'success' });
    } finally {
      setSavingChanges(false);
    }
  }

  const lowerCaseSearchFilter = searchFilter.toLowerCase();
  const validFieldsBySection = baseFieldsModified.reduce((fieldsBySection, field) => {
    if (searchFilter === '' || field.label.toLowerCase().includes(lowerCaseSearchFilter) ||
      field.section.toLowerCase().includes(lowerCaseSearchFilter)) {
      if (!Array.isArray(fieldsBySection[field.section])) fieldsBySection[field.section] = [];
      fieldsBySection[field.section].push(field);
    }

    return fieldsBySection;
  }, {});

  return (
    <React.Fragment>
      <div className="mb-7 d-flex justify-content-between align-items-center">
        <div style={{ maxWidth: '300px' }}>
          <SidebarSearch
            placeholder="Search fields"
            value={searchFilter}
            onChange={e => setSearchFilter(e.target.value)}
          />
        </div>

        <div>
          <Button disabled={savingChanges} type="button" onClick={saveChanges}>
            Save Changes
          </Button>
        </div>
      </div>
      {Object.keys(validFieldsBySection).length > 0 ? (
        <div className="overflow-auto border" style={{ maxHeight: '70vh' }}>
          <table className="table border-0 m-0">
            <thead className="table-light">
              <tr>
                <th className="p-3" style={{ width: '33%' }}>Field Name</th>
                <th className="p-3" style={{ width: '33%' }}>Field Type</th>
                <th className="p-3">Required?</th>
              </tr>
            </thead>
            <tbody>
              {SECTION_ORDER.map((sectionName) => {
                const validFields = validFieldsBySection[sectionName];
                if (validFields === undefined || validFields.length === 0) return null;

                return (
                  <FieldSection
                    fieldsForKey={validFields}
                    key={sectionName}
                    modifiedFields={modifiedFields}
                    removeModelFieldTooltip={removeModelFieldTooltip}
                    sectionName={sectionName}
                    onFieldChanged={toggleField}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty className="bg-white">
          <Empty.Media>
            <FontAwesomeIcon icon={faExclamationCircle} />
          </Empty.Media>
          <Empty.Title>No fields found</Empty.Title>
          <Empty.Content>{`No fields were found that contained ${searchFilter}`}</Empty.Content>
          <Empty.Actions>
            <Button icon={faUndo} variant="ghost" onClick={() => setSearchFilter('')}>
              Reset Search Filter
            </Button>
          </Empty.Actions>
        </Empty>
      )}
    </React.Fragment>
  );
}

CompanyRequiredFields.propTypes = {
  baseFields: baseFieldsArrayShape.isRequired,
  companyOverrides: PropTypes.array.isRequired,
  companyUpdatePath: PropTypes.string.isRequired,
  modelFieldRequirementsPath: PropTypes.string.isRequired,
  removeModelFieldTooltip: PropTypes.array,
};
